<template>
  <div class="document-container">
    <es-loader v-if="loading" style="margin: 15rem" />
    <es-exception v-else-if="loadingError" style="padding: 15rem" margin="0rem" size="4x"
      exceptionMessage="There was an issue. Please contact the admin" />
    <template v-else>
      <div class="columns">
        <div class="column">
          <div class="document" style="height: 100%">
            <search-options-header/>
            <es-exception v-if="!hasModule('PRSID:SRCH:OPTIONS:VIEW')"
              exceptionMessage="Sorry, you are not authorized to access this content." />
            <router-view v-else name="searchOptionsSelection"
              :loadingStudents="loadingStudents"
              :loadingStudentsError="loadingStudentsError"
              :userDistrictInfo="districts"
              :userSchoolInfo="schools"
              @searchParams="getSearchParams($event)"
              @districtCode="getDistrictCode($event)"
              :updateSorter="updateSorter"
              @show="toggleShow($event)"
              @loadingSavedData="toggleLoadingSearchOptions($event)"
              @loadingStudentsError="toggleLoadingStudentsError($event)"
              @result="storeResults($event)"
              @totalCount="getTotalCount($event)"
              @apiSorter="getApiSorter($event)"
              @formDataBody="storeFormData($event)"
              @fileSearchType="getSearchType($event)"/>
          </div>
        </div>
        <!-- Hide Quick Tips in right panel on touch devices -->
        <div class="column is-one-third is-hidden-touch">
          <router-view name="searchOptionsQuickTips" />
        </div>
      </div>
      <div class="columns">
        <div class="column" v-if="this.$route.path.includes('file')">
          <pid-search-batch-results
            :show="show"
            :searchTypeType="fileSearchTypeType"
            :searchTypeCode="fileSearchTypeCode"
            :formDataBody="formDataBody"
            :searchParams="searchParams"
            :updateSorter="updateSorter"
            :totalCount="totalCount"
            :apiSorter="searchApiSorter"
            :page="page"
            :sortField="sortFieldFileSearch"
            :sortOrder="sortOrder"
          />
        </div>
        <div class="column" v-else>
          <pid-search-results v-if="this.student" :loadingStudents="loadingStudents"
            :loadingStudentsError="loadingStudentsError" :show="show" :students="student"
            :userDistrictInfo="districts"
            :userSchoolInfo="schools" :searchParams="searchParams" :districtCode="districtCode"
            :totalCount="totalCount"
            :loadingSearchOptions="loadingSearchOptions"
            :apiSorter="searchApiSorter" :page="page" :sortField="sortField" :sortOrder="sortOrder"
            @loadingStudents="toggleLoadingStudents($event)"
          />
        </div>
      </div>
      <!-- Put Quick Tips on bottom of page for touch devices -->
      <div class="columns">
        <div class="column is-hidden-desktop">
          <router-view name="searchOptionsQuickTips" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsLoader from '@/components/EsLoader.vue';
import PidSearchResults from '@/components/PidSearchResults.vue';
import PidSearchBatchResults from '@/components/PidSearchBatchResults.vue';
import SearchOptionsHeader from '@/views/PersonId/Search/SearchOptionsHeader.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import PermissionManager from '@/mixins/PermissionManager';

export default {
  mixins: [
    BreadcrumbsManager,
    PermissionManager,
  ],
  props: {
    updateSorter: {
      type: Object,
    },
    page: {
      type: Number,
    },
    sortField: {
      type: String,
      default: 'name',
    },
    sortFieldFileSearch: {
      type: String,
      default: 'id',
    },
    sortOrder: {
      type: String,
      default: 'ASC',
    },
  },
  components: {
    EsException,
    EsLoader,
    PidSearchResults,
    PidSearchBatchResults,
    SearchOptionsHeader,
  },
  data() {
    return {
      districts: [],
      districtCode: null,
      loading: false,
      loadingError: false,
      loadingStudents: false,
      loadingStudentsError: false,
      schools: [],
      show: true,
      student: [],
      searchParams: {},
      totalCount: null,
      searchApiSorter: [],
      formDataBody: null,
      fileSearchTypeCode: null,
      fileSearchTypeType: null,
      loadingSearchOptions: false,
    };
  },
  created() {
    this.setBreadcrumb([
      {
        text: 'Home',
        to: { name: 'personId' },
      },
      {
        text: 'Search Options',
        to: {},
      },
    ]);
  },
  mounted() {
    this.onLoad();
  },
  watch: {
    route() {
      this.show = true;
    },
  },
  computed: {
    route() {
      return this.$route.path;
    },
  },
  methods: {
    ...mapActions([
      'getLocationPermissions',
    ]),
    ...mapActions('lookups', [
      'loadEthnicities',
      'loadGenders',
      'loadGrades',
      'loadRaces',
      'loadPidSchoolYears',
      'loadSources',
    ]),
    async onLoad() {
      if (sessionStorage.getItem('storedStudentData') && (this.$route.fullPath === sessionStorage.getItem('storedUrl'))) {
        this.show = false;
        this.student = JSON.parse(sessionStorage.getItem('storedStudentData'));
      }
      this.$router.replace({ query: {} });
      this.loading = true;
      this.loadingError = false;
      try {
        await Promise.all([
          this.loadEthnicities(),
          this.loadGenders(),
          this.loadGrades(),
          this.loadRaces(),
          this.loadPidSchoolYears(),
          this.loadSources(),
        ]);
        const res = await this.getLocationPermissions(this.$store.state.user.id);
        this.districts = res.data[0].districts
          .map(d => ({ code: d.code, location: { name: d.district.location.name } }));
        this.schools = res.data[0].schools
          .map(d => ({ code: d.code, location: { name: d.school.location.name } }));
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    getTotalCount(value) {
      this.searchTotalCount = value;
    },
    getApiSorter(value) {
      this.searchApiSorter = value;
    },
    storeResults(value) {
      this.student = value;
      sessionStorage.setItem('storedStudentData', JSON.stringify(value));
      sessionStorage.setItem('storedUrl', this.$route.fullPath);
    },
    storeFormData(value) {
      this.formDataBody = value;
    },
    toggleLoadingStudents(value) {
      this.loadingStudents = value;
    },
    toggleLoadingStudentsError(value) {
      this.loadingStudentsError = value;
    },
    toggleShow(value) {
      this.show = value;
    },
    toggleLoadingSearchOptions(value) {
      this.loadingSearchOptions = value;
    },
    getSearchParams(value) {
      this.searchParams = value;
    },
    getSearchType(value) {
      this.fileSearchTypeCode = value.code;
      this.fileSearchTypeType = value.type;
    },
    getDistrictCode(value) {
      this.districtCode = value;
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
